import React, { useState, useEffect } from 'react';
import { Shield, ChevronDown, ChevronUp, X } from 'lucide-react';

const COOKIE_KEY = 'phocea_cookie_consent';

const CookieBanner = () => {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_KEY);
    if (!consent) {
      const timer = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(timer);
    }
  }, []);

  const saveConsent = (prefs) => {
    localStorage.setItem(COOKIE_KEY, JSON.stringify({ ...prefs, date: new Date().toISOString() }));
    setVisible(false);
  };

  const acceptAll = () => saveConsent({ necessary: true, analytics: true, marketing: true });
  const rejectAll = () => saveConsent({ necessary: true, analytics: false, marketing: false });
  const saveCustom = () => saveConsent(preferences);

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[9999] animate-in slide-in-from-bottom duration-500"
      data-testid="cookie-banner"
    >
      <div className="mx-auto max-w-5xl px-4 pb-4">
        <div className="rounded-2xl border border-slate-700 bg-slate-900/95 backdrop-blur-xl shadow-2xl shadow-black/40 p-5 md:p-6">
          {/* Header */}
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#DFFF00]/10">
                <Shield className="h-5 w-5 text-[#DFFF00]" />
              </div>
              <h3 className="text-base font-semibold text-white">Gestion des cookies</h3>
            </div>
            <button
              onClick={rejectAll}
              className="text-slate-500 hover:text-slate-300 transition-colors p-1"
              data-testid="cookie-close-btn"
              aria-label="Fermer"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <p className="text-sm text-slate-400 leading-relaxed mb-4">
            Nous utilisons des cookies pour améliorer votre expérience, analyser le trafic et personnaliser le contenu.
            Vous pouvez accepter, refuser ou personnaliser vos préférences.{' '}
            <a href="/confidentialite" className="text-[#DFFF00]/80 hover:text-[#DFFF00] underline underline-offset-2">
              Politique de confidentialité
            </a>
          </p>

          {/* Details toggle */}
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-300 transition-colors mb-4"
            data-testid="cookie-details-toggle"
          >
            {showDetails ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            Personnaliser mes choix
          </button>

          {showDetails && (
            <div className="grid gap-3 mb-5 animate-in fade-in duration-300" data-testid="cookie-details-panel">
              {[
                { key: 'necessary', label: 'Essentiels', desc: 'Authentification, sécurité, navigation. Toujours actifs.', locked: true },
                { key: 'analytics', label: 'Analytiques', desc: 'Mesure d\'audience et amélioration du site.' },
                { key: 'marketing', label: 'Marketing', desc: 'Publicités ciblées et suivi des campagnes.' },
              ].map(({ key, label, desc, locked }) => (
                <label
                  key={key}
                  className="flex items-center justify-between rounded-xl border border-slate-700/60 bg-slate-800/50 px-4 py-3 cursor-pointer hover:border-slate-600 transition-colors"
                >
                  <div>
                    <p className="text-sm font-medium text-white">{label}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{desc}</p>
                  </div>
                  <div className="relative ml-4 shrink-0">
                    <input
                      type="checkbox"
                      checked={preferences[key]}
                      disabled={locked}
                      onChange={() => setPreferences((p) => ({ ...p, [key]: !p[key] }))}
                      className="sr-only peer"
                      data-testid={`cookie-toggle-${key}`}
                    />
                    <div className={`h-6 w-11 rounded-full transition-colors ${locked ? 'bg-[#DFFF00]/30' : 'bg-slate-700 peer-checked:bg-[#DFFF00]/70'}`} />
                    <div className={`absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${preferences[key] ? 'translate-x-5' : 'translate-x-0'}`} />
                  </div>
                </label>
              ))}
            </div>
          )}

          {/* Buttons */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5">
            <button
              onClick={rejectAll}
              className="flex-1 rounded-xl border border-slate-600 px-5 py-2.5 text-sm font-medium text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
              data-testid="cookie-reject-btn"
            >
              Tout refuser
            </button>
            {showDetails && (
              <button
                onClick={saveCustom}
                className="flex-1 rounded-xl border border-[#DFFF00]/30 px-5 py-2.5 text-sm font-medium text-[#DFFF00] hover:bg-[#DFFF00]/10 transition-colors"
                data-testid="cookie-save-custom-btn"
              >
                Enregistrer mes choix
              </button>
            )}
            <button
              onClick={acceptAll}
              className="flex-1 rounded-xl bg-[#DFFF00] px-5 py-2.5 text-sm font-bold text-black hover:bg-[#c8e600] transition-colors"
              data-testid="cookie-accept-btn"
            >
              Tout accepter
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
